import { Channel, Actions as StreamApiActions } from '../actions/streamApi';
import { InboundPolicy } from '../services/stomp/types';

export const WEBSOCKET_ENDPOINTS = {
  STREAM_SERVICE: process.env.REACT_APP_STREAM_SERVICE_URL,
  STOMP_SERVICE: process.env.REACT_APP_STREAM_SERVICE_STOMP_URL,
} as const;

export interface ChannelConfig {
  actionType: StreamApiActions;
  getSubscriptionKey: (data: any) => string;
  validateData: (data: any) => boolean;
  getTopic: (data: any) => string;
  /**
   * `state` is the current Redux state, for channels whose action needs to
   * carry a slice of it.
   */
  processMessage: (parsedData: any, originalData: any, state?: any) => any;
  shouldAutoUnsubscribe?: (parsedData: any) => boolean;
  /**
   * `latest` lets the inbound dispatcher collapse messages that share a
   * subscription key onto the newest one. Only set it where a single message is
   * a complete snapshot of everything the reducers keep for that key --
   * otherwise an intermediate payload is lost.
   */
  inboundPolicy: InboundPolicy;
}

export const CHANNEL_CONFIG: Partial<Record<Channel, ChannelConfig>> = {
  [Channel.SENSOR_READINGS]: {
    actionType: StreamApiActions.RECEIVED_DEVICE_READING,
    // A device can emit different reading types in the same frame.
    inboundPolicy: 'every',
    getSubscriptionKey: (data: any) =>
      `${Channel.SENSOR_READINGS}-${data.vendor}-${data.uuid}`,
    validateData: (data: any) => {
      if (!data.vendor || !data.uuid) {
        console.warn(
          'Vendor and UUID are required to subscribe to sensor readings'
        );
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.SENSOR_READINGS}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_DEVICE_READING,
        data: parsedData.data,
      };
    },
  },
  [Channel.DEVICE_STATUS]: {
    actionType: StreamApiActions.RECEIVED_DEVICE_STATUS,
    // Multiplexed channel: one topic carries online status, paired/scanned
    // sensor lists, the sensor whitelist, actuator output state and gateway
    // states, each read by a different reducer. Two frames in the same flush
    // window are usually different payloads, so collapsing them loses data.
    inboundPolicy: 'every',
    getSubscriptionKey: (data: any) =>
      `${Channel.DEVICE_STATUS}-${data.vendor}-${data.uuid}`,
    validateData: (data: any) => {
      if (!data.vendor || !data.uuid) {
        console.warn(
          'Vendor and UUID are required to subscribe to device status'
        );
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.DEVICE_STATUS}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_DEVICE_STATUS,
        data: parsedData.data,
      };
    },
  },
  [Channel.ENERGY_SENSOR_READING]: {
    actionType: StreamApiActions.RECEIVED_ENERGY_PRO_READING,
    inboundPolicy: 'latest',
    getSubscriptionKey: (data: any) =>
      `${Channel.ENERGY_SENSOR_READING}-${data.uuid}-${data.vendor}`,
    validateData: (data: any) => {
      if (!data.uuid || !data.vendor) {
        console.warn('UUID is required to subscribe to energy sensor reading');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.ENERGY_SENSOR_READING}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any, state?: any) => {
      return {
        type: StreamApiActions.RECEIVED_ENERGY_PRO_READING,
        data: parsedData.data,
        // subscribedEnergyPros formats the reading timestamp with it.
        locale: state?.app?.locale,
      };
    },
  },
  [Channel.CONTROL_BOARD_ILO_STATE]: {
    actionType: StreamApiActions.RECEIVED_CONTROL_BOARD_ILO_STATE,
    inboundPolicy: 'latest',
    getSubscriptionKey: (data: any) =>
      `${Channel.CONTROL_BOARD_ILO_STATE}-${data.uuid}`,
    validateData: (data: any) => {
      if (!data.uuid) {
        console.warn(
          'UUID is required to subscribe to control board ILO state'
        );
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.CONTROL_BOARD_ILO_STATE}/${data.uuid}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_CONTROL_BOARD_ILO_STATE,
        data: parsedData.data,
      };
    },
  },
  [Channel.PROPOSED_READING]: {
    actionType: StreamApiActions.RECEIVED_PROPOSED_READING,
    inboundPolicy: 'latest',
    getSubscriptionKey: (data: any) =>
      `${Channel.PROPOSED_READING}-${data.uuid}-${data.vendor}`,
    validateData: (data: any) => {
      if (!data.uuid || !data.vendor) {
        console.warn('UUID is required to subscribe to proposed reading');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.PROPOSED_READING}/${data.uuid}/${data.vendor}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_PROPOSED_READING,
        data: parsedData.data,
      };
    },
  },
  [Channel.REPORTS_STATUS]: {
    actionType: StreamApiActions.RECEIVED_REPORT_STATUS,
    inboundPolicy: 'every',
    getSubscriptionKey: (data: any) =>
      `${Channel.REPORTS_STATUS}-${data.jobId || 'all'}`,
    validateData: (data: any) => {
      if (!data.jobId) {
        console.warn('Job ID is required to subscribe to reports status');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.REPORTS_STATUS}/${data.jobId}`,
    processMessage: (parsedData: any, originalData: any) => {
      const { jobId } = originalData;
      return {
        type: StreamApiActions.RECEIVED_REPORT_STATUS,
        payload: {
          ...parsedData.data,
          jobId: jobId || parsedData.jobId,
        },
      };
    },
    shouldAutoUnsubscribe: (parsedData: any) =>
      parsedData?.data?.jobState === 'SUCCESS',
  },
  [Channel.DEVICE_INSTALL_LIST]: {
    actionType: StreamApiActions.RECEIVED_DEVICE_INSTALL_LIST,
    inboundPolicy: 'latest',
    getSubscriptionKey: (data: any) =>
      `${Channel.DEVICE_INSTALL_LIST}-${data.uuid || 'all'}`,
    validateData: (data: any) => {
      if (!data.uuid) {
        console.warn('UUID is required to subscribe to device install list');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.DEVICE_INSTALL_LIST}/${data.uuid}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_DEVICE_INSTALL_LIST,
        data: parsedData.data,
      };
    },
  },
  [Channel.FILE_INFO]: {
    actionType: StreamApiActions.RECEIVED_FILE_INFO,
    inboundPolicy: 'every',
    getSubscriptionKey: (data: any) =>
      `${Channel.FILE_INFO}-${data.uuid || 'all'}`,
    validateData: (data: any) => {
      if (!data.uuid) {
        console.warn('UUID is required to subscribe to file info');
        return false;
      }
      return true;
    },
    getTopic: (data: any) =>
      `/topic/subscribe/${Channel.FILE_INFO}/${data.uuid}`,
    processMessage: (parsedData: any, originalData: any) => {
      return {
        type: StreamApiActions.RECEIVED_FILE_INFO,
        data: {
          ...parsedData.data,
          fileUuid: originalData.uuid || parsedData.uuid,
        },
      };
    },
    shouldAutoUnsubscribe: (parsedData: any) =>
      parsedData?.data?.status?.uploadStatus === 'SUCCESS',
  },
} as const;
